import {
  type PredictionMarketChoiceWithProbability,
  type PredictionMarketHistory,
  type PredictionMarketRanking,
  PredictionMarketStatus,
  type PredictionMarketWithProbability,
} from "@roman/shared/types/PredictionMarket";

// Generate demo prediction markets with varied realistic data
export const generateDemoMarkets = (
  count: number,
): (PredictionMarketWithProbability & {
  totalEnteredFreePoints: number;
  numberOfParticipants: number;
})[] => {
  return Array.from({ length: count }).map((_, index) => {
    // Generate varied realistic data for each demo market
    const totalPoints = Math.floor(Math.random() * 50000) + 10000; // 10,000 ~ 60,000
    const participants = Math.floor(Math.random() * 500) + 50; // 50 ~ 550
    const commentCount = Math.floor(Math.random() * 100); // 0 ~ 100

    // Mix of 2-choice and multiple-choice markets
    const isMultipleChoice = index % 3 === 0; // Every 3rd market is multiple choice

    let choices: { id: string; name: string; probability: number }[];

    if (isMultipleChoice) {
      // Generate 3-5 choices with random probabilities
      const numChoices = 3 + Math.floor(Math.random() * 3); // 3-5 choices
      const rawProbabilities = Array.from({ length: numChoices }, () => Math.random());
      const sum = rawProbabilities.reduce((a, b) => a + b, 0);

      choices = rawProbabilities.map((prob, i) => ({
        id: `demo-choice-${i + 1}-${index}`,
        name: `選択肢${i + 1}`,
        probability: prob / sum, // Normalize to sum to 1
      }));
    } else {
      // Two choice market
      const probability1 = 0.3 + Math.random() * 0.4; // 0.3 ~ 0.7
      choices = [
        {
          id: `demo-choice-1-${index}`,
          name: "選択肢1",
          probability: probability1,
        },
        {
          id: `demo-choice-2-${index}`,
          name: "選択肢2",
          probability: 1 - probability1,
        },
      ];
    }

    return {
      id: `demo-${index}`,
      title: `デモ市場${index + 1}`,
      description: `デモ市場${index + 1}の説明`,
      commentCount,
      primaryImageURL: null,
      status: PredictionMarketStatus.Open,
      predictionMarketChoices: choices,
      entryOpenAt: Date.now(),
      entryCloseAt: Date.now() + 86400000,
      realEndTime: null,
      liquidityParameter: 100,
      label: "すべて",
      subLabel: "すべて",
      totalEnteredFreePoints: totalPoints,
      numberOfParticipants: participants,
    };
  });
};

// Generate demo prediction market details
export const generateDemoMarketDetails = (marketId: string) => {
  const index = Number.parseInt(marketId.replace("demo-", "")) || 0;

  // Generate consistent data based on marketId
  const totalPoints = Math.floor((Math.sin(index) * 20000 + 30000) % 50000) + 10000;
  const participants = Math.floor((Math.cos(index) * 200 + 250) % 500) + 50;

  // Mix of 2-choice and multiple-choice markets
  const isMultipleChoice = index % 3 === 0;

  let choices: PredictionMarketChoiceWithProbability[];

  if (isMultipleChoice) {
    const numChoices = 3 + (index % 3);
    const rawProbabilities = Array.from({ length: numChoices }, (_, i) => Math.sin(index + i) * 0.5 + 0.5);
    const sum = rawProbabilities.reduce((a, b) => a + b, 0);

    choices = rawProbabilities.map((prob, i) => ({
      id: `demo-choice-${i + 1}-${index}`,
      name: `選択肢${i + 1}`,
      probability: prob / sum,
      commentCount: Math.floor(Math.random() * 50),
    }));
  } else {
    const probability1 = 0.3 + (Math.sin(index) * 0.2 + 0.2);
    choices = [
      {
        id: `demo-choice-1-${index}`,
        name: "選択肢1",
        probability: probability1,
        commentCount: Math.floor(Math.random() * 50),
      },
      {
        id: `demo-choice-2-${index}`,
        name: "選択肢2",
        probability: 1 - probability1,
        commentCount: Math.floor(Math.random() * 50),
      },
    ];
  }

  // Generate history with 20 snapshots over the last 24 hours
  const now = Date.now();
  const timestampCount = 20;
  const timestamps: number[] = [];
  const history: PredictionMarketHistory = { timestamps };

  for (let i = 0; i < timestampCount; i++) {
    timestamps.push(now - (timestampCount - i) * 3600000); // Hourly snapshots
  }

  // Generate probability progression for each choice
  for (const choice of choices) {
    const choiceHistory: number[] = [];
    const endProbability = choice.probability;
    const startProbability = 1 / choices.length; // Start equal

    for (let i = 0; i < timestampCount; i++) {
      const progress = i / (timestampCount - 1);
      // Add some randomness to make it more realistic
      const noise = Math.sin(index + i * 0.5) * 0.05;
      const probability = startProbability + (endProbability - startProbability) * progress + noise;
      choiceHistory.push(Math.max(0, Math.min(1, probability)));
    }

    // Normalize each snapshot to sum to 1
    history[choice.id] = choiceHistory;
  }

  // Normalize probabilities at each timestamp to sum to 1
  for (let i = 0; i < timestampCount; i++) {
    let sum = 0;
    for (const choice of choices) {
      sum += history[choice.id][i];
    }
    for (const choice of choices) {
      history[choice.id][i] /= sum;
    }
  }

  const predictionMarketHistory = {
    predictionMarket: {
      id: marketId,
      title: `デモ市場${index + 1}`,
      description: `これはデモ市場${index + 1}の説明です。\n\nこの市場では様々な選択肢から予測を行うことができます。ポイントを使って予測に参加し、結果が正しければ報酬を獲得できます。`,
      commentCount: Math.floor(Math.random() * 100),
      primaryImageURL: null,
      status: PredictionMarketStatus.Open,
      predictionMarketChoices: choices,
      entryOpenAt: now - 86400000,
      entryCloseAt: now + 86400000,
      realEndTime: null,
      liquidityParameter: 100,
      label: "すべて",
      subLabel: "すべて",
    },
    predictionMarketHistory: history,
    totalEnteredFreePoints: totalPoints,
    numberOfParticipants: participants,
  };

  // Generate demo rankings
  const predictionMarketRanking: PredictionMarketRanking = {
    status: "live",
    marketRanks: choices.map((choice) => ({
      predictionMarketChoiceId: choice.id,
      predictionMarketChoiceName: choice.name,
      ranks: Array.from({ length: 15 }, (_, rankIndex) => ({
        userId: `demo-user-${rankIndex}`,
        userName: `ユーザー${rankIndex + 1}`,
        userPhotoUrl: null,
        rank: rankIndex + 1,
        freePointsEntered: Math.floor(Math.random() * 5000) + 1000,
        score: Math.floor(Math.random() * 1000) + 100,
        potentialPrize: Math.floor(Math.random() * 10000) + 1000,
      })),
    })),
  };

  return {
    predictionMarketHistory,
    predictionMarketRanking,
  };
};
